'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import type { LmsDataWithAdsType, ModelType } from '@/app/api/lms/type';
import { Button } from '@/app/components/button';
import { LocationSelector } from '@/app/components/form-kit/fields/location-selector';
import { PrivacyField } from '@/app/components/form-kit/fields/privacy-field';
import type { LeadFormProps } from '@/app/components/form-kit/types';
import { Checkbox } from '@/app/components/forms/checkbox';
import { Radio } from '@/app/components/forms/radio';
import { Select } from '@/app/components/forms/select';
import { TextField } from '@/app/components/forms/text-field';
import { AVAILABLE_TIMES, FORM_LABELS, FORM_MESSAGES, FORM_PLACEHOLDERS, VEHICLE_TYPES } from '@/app/constants/forms';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { useGtmReady } from '@/app/hooks/useGtmReady';
import { useForm } from '@/app/lib/forms/hooks/useForm';
import type { LeadFormValues } from '@/app/lib/forms/schemas/lead-form.schema';
import { useFormTracking } from '@/app/lib/forms/tracking/useFormTracking';
import type { FormValues } from '@/app/lib/forms/types/form.types';
import { createSchemaFieldCheckers } from '@/app/lib/forms/utils/schema-fields';
import { createFullnameFieldValidator, transformToOnlyNumber } from '@/app/lib/forms/utils/validations.function';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, safeText } from '@/app/utils/createDataGA';
import { trackFormSubmission } from '@/app/utils/form-tracking';
import { getAdsTracking, getSkipCallCenter } from '@/app/utils/lms';
import { safeCall } from '@/app/utils/safeCall';
import logger from '@/lib/logger';
import { EMAIL_NOTIFICATION_FORM_IDS } from '@/services/contentstack/common/form';
import { sendLeadNotification } from '@/services/email/sendLeadNotification';
import { createLead } from '@/services/lms/createLead';
export const LeadForm = <T extends z.ZodType<LeadFormValues>,>({
  theme = 'light',
  campaignVersion = '1',
  branchCode: defaultBranchCode = '',
  className = '',
  formId,
  formName,
  formLayout = 'single-column',
  hideSubmitButton = false,
  isDisabled = false,
  loading: externalLoading = false,
  model,
  provinces = [],
  remark = '',
  schema,
  vehicleType,
  title,
  dealerSelectorLabel,
  privacy,
  fbModel,
  tracking,
  formTracking,
  initialValues,
  additionalData,
  privacyTooltipClassName,
  onBeforeSubmit,
  onError,
  onSubmit: customOnSubmit,
  onSuccess,
  onFormReady
}: LeadFormProps<T>) => {
  const {
    hasField,
    isFieldRequired
  } = createSchemaFieldCheckers(schema);
  const hasLineId = hasField('lineId');
  const hasEmail = hasField('email');
  const hasProvince = hasField('province') && provinces.length > 0;
  const hasLocation = hasField('dealer');
  const hasAvailableTime = hasField('availableTime');
  const hasPrivacy = hasField('privacy');
  const hasMobileAsLine = hasField('mobileAsLine');
  const hasFullname = hasField('fullname');
  const hasMobile = hasField('mobile');
  const isFullNameRequired = isFieldRequired('fullname');
  const isMobileRequired = isFieldRequired('mobile');
  const isEmailRequired = isFieldRequired('email');
  const isLineIdRequired = isFieldRequired('lineId');
  const isProvinceRequired = isFieldRequired('province');
  const safeFormName = safeText(formName) ?? '';
  const [internalLoading, setInternalLoading] = useState<boolean>(false);
  const [isPrivacyTooltipOpen, {
    open: openPrivacyTooltip,
    close: closePrivacyTooltip
  }] = useDisclosure(false);
  const loading = externalLoading || internalLoading;

  // Initialize GTM event
  useGtmReady(() => {
    sendGTMEvent({
      event: 'form-initiated',
      formId: formId,
      formName: safeFormName
    });
  });
  const tracker = useFormTracking(formTracking && {
    formId,
    formName: safeFormName,
    ...formTracking
  });
  // Read at commit time, not during render.
  const observeFormView = useCallback((element: HTMLFormElement | null) => tracker.observeView(element), [tracker]);
  const {
    registerRef,
    validate,
    handleSubmit,
    setValue,
    clearErrors,
    values,
    formState
  } = useForm({
    schema,
    tracking: tracker,
    initialValues: {
      availableTime: AVAILABLE_TIMES[0],
      model: model,
      privacy: false,
      ...initialValues
    } as Partial<FormValues<T>>,
    validators: {
      fullname: createFullnameFieldValidator(() => formState),
      mobile: {
        transform: (value: string) => transformToOnlyNumber(value)
      },
      lineId: {
        transform: (value: string) => (value || '').trim().toLowerCase()
      }
    }
  });
  const formValues = values as LeadFormValues;
  const handleVehicleTypeChange = (value: string) => {
    setValue('model', value as ModelType);
  };
  const submitLeadForm = async (formId: string, leadData: LmsDataWithAdsType) => {
    if (!(EMAIL_NOTIFICATION_FORM_IDS as readonly string[]).includes(formId)) {
      return await createLead(leadData);
    }
    await sendLeadNotification({
      formId,
      formName: safeFormName,
      fullname: leadData.firstName,
      mobile: leadData.mobile,
      lineId: leadData.lineId ?? '',
      email: leadData.email ?? '',
      availableTime: leadData.availableTime || ''
    });
  };
  const handleFormSubmit = async (data: FormValues<T>) => {
    const formData = data as LeadFormValues;
    if (customOnSubmit) {
      return customOnSubmit(formData);
    }
    setInternalLoading(true);
    let submitStartedAt: number | undefined;
    try {
      const province = provinces.find(p => p.uid === formData.province);
      const districts = province?.districts || [];
      let district = districts.find((d: any) => d.uid === formData.district);
      let dealer = district?.dealers?.find((dl: any) => dl.branchCode === formData.dealer);
      if (!dealer) {
        district = districts.find((d: any) => d.dealers?.some((dl: any) => dl.branchCode === formData.dealer));
        dealer = district?.dealers?.find((dl: any) => dl.branchCode === formData.dealer);
      }
      const leadRemark = hasLocation ? remark || '' : remark || province?.name_th || '';
      const leadData: LmsDataWithAdsType = {
        ctsCampaignName: safeFormName,
        ctsCampaignId: formId,
        ctsCampaignVersion: campaignVersion || '1',
        firstName: formData.fullname ?? '',
        mobile: formData.mobile ?? '',
        availableTime: formData.availableTime,
        branchCode: formData.dealer || defaultBranchCode,
        email: formData.email || '',
        ...additionalData,
        ...getAdsTracking(),
        ...getSkipCallCenter()
      };
      if (leadRemark) {
        leadData.remark = leadRemark;
      }
      if (formData.model) {
        leadData.model = formData.model;
      }
      if (formData.mobileAsLine) {
        leadData.lineId = formData.mobile;
      } else if (formData.lineId) {
        leadData.lineId = formData.lineId;
      }
      submitStartedAt = performance.now();
      const submitResult = await submitLeadForm(formId, leadData);
      tracker.onSuccess({
        leadId: submitResult?.leadId,
        responseMs: performance.now() - submitStartedAt
      });
      submitStartedAt = undefined;
      const trackingData = {
        formId,
        formName: safeFormName,
        id: formData.dealer ?? '',
        dealerName: dealer?.name_th ?? '',
        city: province?.name_th ?? '',
        district: district?.name_th ?? ''
      };
      sendGTMEvent({
        event: 'form-success',
        ...trackingData
      });
      trackFormSubmission({
        ...trackingData,
        contentIds: safeFormName,
        model: formData.model ?? '',
        branchCode: formData.dealer ?? '',
        fbModel: fbModel ?? safeFormName
      });
      safeCall(onSuccess, {
        data: leadData,
        leadId: submitResult?.leadId,
        province: province?.name_th,
        district: district?.name_th,
        dealer: dealer?.name_th
      });
    } catch (error) {
      logger.error(error);
      // Only the lead API failing counts, not an error before or after it.
      if (submitStartedAt !== undefined) {
        tracker.onFailed({
          errorType: 'api_error',
          responseMs: performance.now() - submitStartedAt
        });
      }
      safeCall(onError, error as Error);
    } finally {
      setInternalLoading(false);
    }
  };
  const triggerPrivacyWarning = () => {
    if (isPrivacyTooltipOpen) return;
    openPrivacyTooltip();
  };
  const submitHandler = (event?: FormEvent) => {
    if (hasPrivacy && !formValues.privacy) {
      triggerPrivacyWarning();
    }
    if (onBeforeSubmit && !onBeforeSubmit()) {
      validate();
      return;
    }
    handleSubmit(handleFormSubmit)(event);
  };
  const submitHandlerRef = useRef(submitHandler);
  const stableSubmitHandler = useCallback((event?: FormEvent) => {
    submitHandlerRef.current(event);
  }, []);
  const handlePrivacyCheckAndSubmit = (event?: FormEvent) => {
    event?.preventDefault();
    submitHandler(event);
  };
  const isDisabledSubmit = loading || formState.isSubmitting || (isDisabled ?? false);
  useEffect(() => {
    if (!onFormReady || typeof onFormReady !== 'function') return;
    onFormReady(stableSubmitHandler, {
      isLoading: loading || formState.isSubmitting,
      isDisabled: isDisabledSubmit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, formState.isSubmitting, isDisabledSubmit]);
  useEffect(() => {
    if (!vehicleType?.isEnabled) return;
    const initialSearchParams = new URLSearchParams(window.location.search);
    switch (initialSearchParams.get('model')) {
      case 'ppv':
        setValue('model', 'ppv');
        break;
      case 'pickup':
        setValue('model', 'pickup');
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType?.isEnabled]);
  const isTwoColumn = formLayout === 'two-column';
  return <form ref={observeFormView} id={formId} className={cn('space-y-4', className)} onSubmit={handlePrivacyCheckAndSubmit} noValidate {...createDataAttributes({
    test: 'lead-form'
  })}>
      {!!title && <h3 className="text-body-1-fixed font-noto-bold text-gray-700" {...createDataAttributes({
      test: 'title'
    })}>
          {title}
        </h3>}

      {!!vehicleType?.isEnabled && <Radio name="model" theme={theme} label={vehicleType?.label === null ? undefined : FORM_LABELS.selectVehicle} value={formValues.model || ''} options={[{
      value: 'pickup',
      label: VEHICLE_TYPES.pickup,
      checked: formValues.model === 'pickup'
    }, {
      value: 'ppv',
      label: VEHICLE_TYPES.ppv,
      checked: formValues.model === 'ppv'
    }]} error={false} disabled={false} onChange={handleVehicleTypeChange}
    // Same event name as the submit button when the page sets none.
    tracking={{
      ...tracking,
      eventName: tracking?.eventName ?? 'form-click'
    }} />}

      <div className="grid grid-cols-2 gap-4 *:scroll-mt-16">
        {!!hasFullname && <TextField ref={registerRef('fullname')} name="fullname" id="fullname" type="text" theme={theme} label={FORM_LABELS.fullname} placeholder={FORM_PLACEHOLDERS.fullname} value={formValues.fullname || ''} maxLength={50} required={isFullNameRequired} error={!!formState.errors.fullname} errorMessage={formState.errors.fullname} disabled={loading} onChange={e => setValue('fullname', e.target.value)} groupClassName={cn({
        'col-span-2 lg:col-span-2': !isTwoColumn,
        'col-span-2 lg:col-span-1': isTwoColumn
      })} groupAttributes={createDataAttributes({
        test: 'fullname'
      })} />}

        {!!hasMobile && <TextField ref={registerRef('mobile')} name="mobile" id="input-mobile" // NOTE: use with tagmanager DOMELM_Lead_Mobile
      type="tel" theme={theme} label={FORM_LABELS.mobile} placeholder={FORM_PLACEHOLDERS.mobile} value={formValues.mobile || ''} maxLength={10} required={isMobileRequired} error={!!formState.errors.mobile} errorMessage={formState.errors.mobile} disabled={loading} onChange={e => setValue('mobile', e.target.value)} data-form-type="other" // NOTE: [TISW-10581] To prevent Dashlane extension popup that cause overflow layout bug
      groupClassName={cn({
        'col-span-2 lg:col-span-2': !isTwoColumn && !hasLineId,
        'col-span-2 lg:col-span-1': isTwoColumn
      })} groupAttributes={createDataAttributes({
        test: 'mobile'
      })} />}

        {!!hasLineId && <TextField ref={registerRef('lineId')} name="lineId" id="lineId" type="text" theme={theme} label={FORM_LABELS.lineId} secondaryLabel={isLineIdRequired ? undefined : '(ถ้ามี)'} placeholder={FORM_PLACEHOLDERS.lineId} value={formValues.lineId || ''} error={!!formState.errors.lineId} errorMessage={formState.errors.lineId} disabled={!!formValues.mobileAsLine || loading} onChange={e => setValue('lineId', e.target.value)} maxLength={20} groupClassName={cn({
        'col-span-1': !isTwoColumn,
        'col-span-2': isTwoColumn
      })} groupAttributes={createDataAttributes({
        test: 'lineId'
      })} />}
      </div>

      {!!hasMobileAsLine && <Checkbox name="mobileAsLine" theme={theme} options={[{
      name: 'mobileAsLine',
      label: hasLineId ? FORM_MESSAGES.mobileAddLine : FORM_MESSAGES.mobileAsLine,
      checked: !!formValues.mobileAsLine
    }]} disabled={loading} onChangeInput={e => {
      const checked = e.target.checked;
      setValue('mobileAsLine', checked);
      if (checked) {
        setValue('lineId', '');
        clearErrors('lineId');
      }
    }} tracking={tracking} />}

      {!!hasEmail && <TextField ref={registerRef('email')} name="email" id="input-email" // NOTE: use with tagmanager DOMELM_Lead_Email
    type="email" theme={theme} label={FORM_LABELS.email} secondaryLabel={isEmailRequired ? undefined : '(ถ้ามี)'} placeholder={FORM_PLACEHOLDERS.email} value={formValues.email || ''} error={!!formState.errors.email} errorMessage={formState.errors.email} disabled={loading} onChange={e => setValue('email', e.target.value)} />}

      {!!isTwoColumn && !!hasProvince && <p id="dealer-selector-title" className={cn('text-body-1-fixed font-noto-bold mt-6 block', {
      'text-white': theme === 'dark'
    })} {...createDataAttributes({
      test: 'dealer-selector-title'
    })}>
          {dealerSelectorLabel ?? 'เลือกผู้จำหน่าย'}
        </p>}

      {!!(hasLocation || hasAvailableTime) && <div className={cn('grid gap-4 *:scroll-mt-16 lg:grid-cols-2')} {...createDataAttributes({
      test: 'location-selector-group'
    })}>
          {!!hasProvince && <LocationSelector formLayout={formLayout} provinces={provinces} theme={theme} values={values} setValue={(field: 'province' | 'district' | 'dealer', value: string) => setValue(field, value)} registerRef={registerRef} errors={formState.errors} disabled={loading} provinceOnly={!hasLocation} required={isProvinceRequired} dealerColSpan={hasAvailableTime ? 1 : 2} />}

          {!!hasAvailableTime && <Select name="availableTime" theme={theme} label={FORM_LABELS.availableTime} value={formValues.availableTime || ''} options={AVAILABLE_TIMES.map(item => ({
        label: item,
        value: item
      }))} error={false} disabled={loading} hidePlaceholder onChange={e => setValue('availableTime', e.target.value)} groupClassName={cn({
        'col-span-2 lg:col-span-2': !isTwoColumn,
        'col-span-2 lg:col-span-1': isTwoColumn
      })} {...createDataAttributes({
        test: 'available-time'
      })} />}
        </div>}

      {!!hasPrivacy && <PrivacyField registerRef={registerRef} theme={theme} checked={!!formValues.privacy} tracking={tracking} isError={!!formState.errors.privacy} isLoading={loading} isOpen={isPrivacyTooltipOpen} requiredMessage={privacy?.requiredMessage} onClose={closePrivacyTooltip} tooltipClassName={privacyTooltipClassName} onChange={checked => {
      if (isPrivacyTooltipOpen) {
        closePrivacyTooltip();
      }
      setValue('privacy', checked);
    }}>
          {privacy?.children}
        </PrivacyField>}

      {!hideSubmitButton && <Button type="submit" className="w-full lg:w-auto" disabled={isDisabledSubmit} {...createDataAttributes({
      test: 'submit-button'
    })} {...createDataGA({
      eventName: tracking?.eventName ?? 'form-click',
      click: true,
      text: FORM_MESSAGES.submit,
      value: formId
    })}>
          {loading || formState.isSubmitting ? FORM_MESSAGES.submitting : FORM_MESSAGES.submit}
        </Button>}
    </form>;
};